import { Building2, Check, ChevronDown } from "lucide-react";
import { useMemo } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import type { AuthSurface } from "@/lib/firebase";
import { coreStore } from "@/lib/stores/core-store";
import { opsUiStore } from "@/lib/stores/ops-ui-store";
import { uiStore } from "@/lib/stores/ui-store";

/**
 * Which session's workspace selection this selector drives. `/operations` holds
 * its own auth session and its own `currentWorkspaceId` (TECH-3151), so it must
 * not write the `/app` session's, and it must not go through
 * `coreStore.selectWorkspace`, whose tail is `/app`'s sequential connect chain.
 * The ops route runs its own workspace preamble off this value instead.
 */
export function WorkspaceSelector({
	surface = "app",
}: {
	surface?: AuthSurface;
} = {}) {
	const isOps = surface === "operations";
	const workspacesMap = useStore(coreStore, (s) => s.workspaces);
	const opsWorkspaceIds = useStore(coreStore, (s) => s.opsWorkspaceIds);
	// `/app` offers every Workspace the caller participates in, which is the
	// question its gate asks (TECH-3260). `/operations` offers only the ones a
	// tool can be opened in, which is the question ITS gate asks (TECH-3284).
	// Intersected here rather than filtered into the store, because the map is
	// shared with `/app` and patched a row at a time by `workspace-announce`.
	const workspaces = useMemo(() => {
		const all = Array.from(workspacesMap.values());
		if (!isOps) return all;
		if (!opsWorkspaceIds) return [];
		const eligible = new Set(opsWorkspaceIds);
		return all.filter((w) => eligible.has(w.id));
	}, [workspacesMap, isOps, opsWorkspaceIds]);
	const appWorkspaceId = useStore(uiStore, (s) => s.currentWorkspaceId);
	const opsWorkspaceId = useStore(opsUiStore, (s) => s.currentWorkspaceId);
	const setOpsWorkspaceId = useStore(
		opsUiStore,
		(s) => s.setCurrentWorkspaceId
	);
	const selectWorkspace = useStore(coreStore, (s) => s.selectWorkspace);

	const currentWorkspaceId = isOps ? opsWorkspaceId : appWorkspaceId;
	const onSelectWorkspace = isOps ? setOpsWorkspaceId : selectWorkspace;

	const currentWorkspace = useMemo(
		() => workspaces.find((w) => w.id === currentWorkspaceId),
		[workspaces, currentWorkspaceId]
	);

	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const Wrapper = isCollapsed ? "div" : Card;

	return (
		<Wrapper className={isCollapsed ? "p-2" : "p-2"}>
			<SidebarMenu className={isCollapsed ? "" : "min-w-0 flex-1"}>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton asChild>
								<Button
									className="w-full justify-start"
									data-tour="workspace-selector"
									variant="ghost"
								>
									<Building2 className="size-5" />
									<span className="truncate">
										{currentWorkspace?.name ?? "Select Workspace"}
									</span>
									<ChevronDown className="ml-auto size-3.5" />
								</Button>
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start" className="w-56">
							<DropdownMenuLabel>Workspaces</DropdownMenuLabel>
							<DropdownMenuSeparator />
							{workspaces.length === 0 ? (
								<DropdownMenuItem disabled>
									No workspaces available
								</DropdownMenuItem>
							) : (
								workspaces.map((workspace) => (
									<DropdownMenuItem
										className={
											workspace.id === currentWorkspaceId ? "bg-accent" : ""
										}
										key={workspace.id}
										onClick={() => {
											if (workspace.id !== currentWorkspaceId) {
												onSelectWorkspace(workspace.id);
											}
										}}
									>
										<Building2 className="size-3.5" />
										{workspace.name}
										{workspace.id === currentWorkspaceId && (
											<Check className="ml-auto size-3.5" />
										)}
									</DropdownMenuItem>
								))
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
		</Wrapper>
	);
}
